import { motion } from 'motion/react';
import { Img } from 'react-image';
import { useInView } from 'react-intersection-observer';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';

const forceLoading =
  typeof window !== 'undefined' &&
  localStorage.getItem('forceImageLoading') === 'true';

type LazyImageProps = {
  width?: string | number;
  height?: string | number;
  src: string | string[];
  onLoad?: () => void;
  isDarkMode?: boolean;
  onClick?: () => void;
};

export const LazyImage: React.FC<LazyImageProps> = ({
  width,
  height,
  src,
  onLoad,
  isDarkMode,
  onClick,
  ...rest
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true // this is not necessary, but just found out that we don't need to hide image once loaded
  });

  const loaderContainer = (children: React.ReactNode) => (
    <motion.div
      key={`loader-${src}`}
      className="w-full h-full flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      {children}
    </motion.div>
  );

  const container = (children: React.ReactNode) => (
    <motion.div
      key={`container-${src}`}
      className="lazy-image__container [&_img]:inline [&_img]:max-w-none"
      style={{
        // todo v5 should have cleaned version
        height: '100%'
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );

  return (
    <div // todo v5 should have cleaned version
      ref={ref}
      className="lazy-image tailwind-root" // be careful, tailwind-root will change image layout defaults!
      style={{
        position: 'relative',
        // @ts-expect-error not migrating js code right now
        paddingBottom: `${(height / width) * 100}%`,
        height: '100%'
      }}
      onClick={onClick}
    >
      {forceLoading ? (
        loaderContainer(<LoadingSpinner />)
      ) : (
        <Img
          {...rest}
          // @ts-expect-error see old working behavior to disable image loading useInView
          src={inView && src}
          width={width}
          height={height}
          onLoad={onLoad}
          loader={<LoadingSpinner />}
          loaderContainer={loaderContainer}
          container={container}
          style={{
            // this is a hack since tailwind preflight sets height to auto. more info: https://stackoverflow.com/questions/44010645/remove-height-auto-using-css
            height: `${height}px`
          }}
        />
      )}
    </div>
  );
};

export default LazyImage;
